type ParsedEvent = Record<string, unknown>;

type OutputTextDeltaEvent = {
  type: 'response.output_text.delta';
  delta: string;
};

type FileCitationAnnotation = {
  type: 'file_citation';
  file_id: string;
  filename: string;
  index?: number;
};

type OutputTextAnnotationAddedEvent = {
  type: 'response.output_text.annotation.added';
  annotation: FileCitationAnnotation;
  annotation_index?: number;
};

type OutputTextDoneEvent = {
  type: 'response.output_text.done';
};

type ResponseCompletedEvent = {
  type: 'response.completed';
  response?: unknown;
};

type EndEvent = {
  type: 'end';
  message?: string;
};

type ReasoningSummaryTextDeltaEvent = {
  type: 'response.reasoning_summary_text.delta';
  delta: string;
};

type OpenAIErrorResponse = {
  type: 'error';
  code: string;
  message: string;
  param: string | null;
  sequence_number?: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getStringFrom = (value: unknown, key: string): string | undefined => {
  if (!isRecord(value)) return undefined;
  const raw = value[key];
  return typeof raw === 'string' ? raw : undefined;
};

const getNumberFrom = (value: unknown, key: string): number | undefined => {
  if (!isRecord(value)) return undefined;
  const raw = value[key];
  return typeof raw === 'number' ? raw : undefined;
};

const getEventType = (value: unknown): string | undefined =>
  getStringFrom(value, 'type');

const isOpenAIError = (
  parsedEvent: unknown,
): parsedEvent is OpenAIErrorResponse => {
  if (!isRecord(parsedEvent)) return false;
  return (
    parsedEvent.type === 'error' &&
    typeof getStringFrom(parsedEvent, 'code') === 'string' &&
    typeof getStringFrom(parsedEvent, 'message') === 'string'
  );
};

const isOutputTextDeltaEvent = (
  event: ParsedEvent,
): event is OutputTextDeltaEvent =>
  getEventType(event) === 'response.output_text.delta' &&
  typeof getStringFrom(event, 'delta') === 'string';

const isOutputTextAnnotationAddedEvent = (
  event: ParsedEvent,
): event is OutputTextAnnotationAddedEvent => {
  if (getEventType(event) !== 'response.output_text.annotation.added') {
    return false;
  }
  const annotation = (event as { annotation?: unknown }).annotation;
  return (
    getStringFrom(annotation, 'type') === 'file_citation' &&
    typeof getStringFrom(annotation, 'file_id') === 'string' &&
    typeof getStringFrom(annotation, 'filename') === 'string'
  );
};

const isOutputTextDoneEvent = (
  event: ParsedEvent,
): event is OutputTextDoneEvent =>
  getEventType(event) === 'response.output_text.done';

const isResponseCompletedEvent = (
  event: ParsedEvent,
): event is ResponseCompletedEvent =>
  getEventType(event) === 'response.completed';

const isEndEvent = (event: ParsedEvent): event is EndEvent =>
  getEventType(event) === 'end' &&
  getStringFrom(event, 'message') === 'completed';

const isReasoningSummaryTextDeltaEvent = (
  event: ParsedEvent,
): event is ReasoningSummaryTextDeltaEvent =>
  getEventType(event) === 'response.reasoning_summary_text.delta' &&
  typeof getStringFrom(event, 'delta') === 'string';

export type { ParsedEvent, OpenAIErrorResponse, FileCitationAnnotation };
export {
  isRecord,
  getStringFrom,
  getNumberFrom,
  getEventType,
  isOpenAIError,
  isEndEvent,
  isOutputTextDeltaEvent,
  isOutputTextDoneEvent,
  isResponseCompletedEvent,
  isOutputTextAnnotationAddedEvent,
  isReasoningSummaryTextDeltaEvent,
};
